import { AddScriptProps, Config, LocalesProps, OptionCookieProps, ServiceProps } from '../types';
import Alert from '../components/Alert';
import BoxManager from '../components/BoxManager';
import Icon from '../components/Icon';
import { Services } from './Services';
import { I18n } from 'i18n-js';
import translations from '../locales';

export class ConsentBoxManager {
  locales: LocalesProps | undefined;

  i18n = new I18n(translations);

  services = new Services();

  defaultConfig: Config = {
    websiteTitle: '',
    policyUri: null,
    locale: 'en',
    useExternalJs: false,
  };

  config = this.defaultConfig;

  needReloadPageOnClose = false;

  setCookie(options: OptionCookieProps[]) {
    const expDays = 365;
    const date = new Date();
    date.setTime(date.getTime() + expDays * 24 * 60 * 60 * 1000);

    const expires = 'expires=' + date.toUTCString();
    const value = JSON.stringify(options);

    document.cookie = 'ckm' + '=' + value + '; ' + expires + '; path=/';
  }

  getCookieByName(name: string) {
    const cookieString = document.cookie.match(name + '=[^;]+');
    const cookie = cookieString ? cookieString[0] : cookieString;
    return cookie?.split('=')[1];
  }

  readCookie(): OptionCookieProps[] {
    const cookie = this.getCookieByName('ckm');
    if (cookie) {
      return JSON.parse(cookie);
    }
    return [];
  }

  populateOptions(element: HTMLElement): void {
    const cookieOptions = this.readCookie();
    cookieOptions
      ?.filter((option) => option.value !== 'wait')
      .forEach((option) => {
        const checkbox = element.querySelector(`[data-service=${option.service}]`) as HTMLInputElement;
        if (checkbox) {
          checkbox.checked = option.value === 'true';
        }
      });
  }

  executeService(service: ServiceProps) {
    if (!service.executed) {
      service.js(); // Execute script defined in service
      service.executed = true;
    }
  }

  addScript({
    url,
    id,
    callback = undefined,
    execute = true,
    attrName = undefined,
    attrVal = undefined,
    internal = false,
  }: AddScriptProps) {
    const script = document.createElement('script');
    let done = false;

    if (!execute) {
      if (typeof callback === 'function') {
        callback();
      }
    } else {
      script.id = id !== undefined ? id : '';
      script.async = true;
      script.src = url;

      if (attrName !== undefined && attrVal !== undefined) {
        script.setAttribute(attrName, attrVal);
      }

      if (typeof callback === 'function') {
        if (!this.config.useExternalJs || !internal) {
          if (!done) {
            script.addEventListener('load', () => {
              done = true;
              callback();
            });
          }
        } else {
          callback();
        }
      }

      if (!this.config.useExternalJs || !internal) {
        document.getElementsByTagName('head')[0].appendChild(script);
      }
    }
  }

  async init(config: Config) {
    this.i18n.defaultLocale = config.locale;
    this.i18n.locale = config.locale;

    this.config = { ...this.defaultConfig, ...config };

    // Don't display the cookie policy on the page who talk about cookie policies
    if (window.location.href === this.config.policyUri) {
      return;
    }

    let html = '';
    html += '<div class="ecodigo-ckm-wrapper">';
    html += '<div class="ecodigo-ckm-backdrop"></div>';
    html += Alert(this.i18n, this.config);
    html += BoxManager(this.i18n, this.services.values, this.config);
    html += '</div>';
    html += Icon();

    document.body.insertAdjacentHTML('beforeend', html);

    const cookies = this.readCookie();
    const allowedService = cookies
      .map((cookie) => (cookie.value === 'true' ? cookie.service : null))
      .filter((cookie) => cookie);

    const servicesToExecute = this.services.values.filter((service) => service.enabled);
    servicesToExecute?.forEach((service) => {
      if (service.type === 'mandatory' || allowedService.includes(service.type)) {
        this.executeService(service);
      }
    });

    const body = document.querySelectorAll('html, body') as unknown as HTMLElement[];
    const wrapper = document.querySelector('.ecodigo-ckm-wrapper') as HTMLElement;
    const backdrop = document.querySelector('.ecodigo-ckm-backdrop') as HTMLElement;
    const boxManager = wrapper?.querySelector('.ecodigo-ckm-box-manager') as HTMLElement;
    const alert = wrapper?.querySelector('.ecodigo-ckm-alert') as HTMLElement;
    const icon = document.querySelector('.ecodigo-ckm-icon') as HTMLElement;

    // Methods
    const closeAlert = () => {
      alert.classList.remove('--show');
      wrapper.classList.remove('--show-backdrop', '--show');
      body.forEach((item) => {
        item.style.overflowY = '';
      });
    };

    const openAlert = () => {
      alert.classList.add('--show');
      wrapper.classList.add('--show-backdrop', '--show');
      body.forEach((item) => {
        item.style.overflowY = 'hidden';
      });
    };

    const closeBoxManager = () => {
      boxManager.classList.remove('--show');
      wrapper.classList.remove('--show-backdrop', '--show');

      backdrop.removeEventListener('click', handleCloseBoxManager);

      body.forEach((item) => {
        item.style.overflowY = '';
      });

      if (this.needReloadPageOnClose) {
        location.reload();
      }
    };

    const handleCloseBoxManager = (event: MouseEvent) => {
      if (event.target === backdrop) {
        closeBoxManager();
      }
    };

    const saveOptions = () => {
      const newOptions: OptionCookieProps[] = [];
      const oldOptions = this.readCookie();

      boxManager
        .querySelectorAll('.ecodigo-ckm-box-manager__services-list__item__header__switch .ecodigo-ckm-switch__input')
        .forEach((checkbox) => {
          const serviceType = checkbox.getAttribute('data-service') as string;
          const checked = (checkbox as HTMLInputElement).checked;

          newOptions.push({
            service: serviceType,
            value: checked ? 'true' : 'false',
          });

          if (checked) {
            const filteredServices = this.services.values.filter(
              (service) => service.type === serviceType && !service.enabled,
            );
            filteredServices?.forEach((service) => {
              this.services.enable(service.key);
            });
          } else {
            const filteredServices = this.services.values.filter(
              (service) => service.type === serviceType && service.enabled,
            );
            filteredServices?.forEach((service) => {
              this.services.disable(service.key);
            });
          }
        });

      this.setCookie(newOptions);

      // Compare new Options and old Option to check if we need to reload page
      oldOptions.forEach((oldOption) => {
        const newOption = newOptions.find((item) => item.service === oldOption.service);
        if (newOption?.value === 'false' && oldOption.value === 'true') {
          this.needReloadPageOnClose = true;
        }
      });

      const servicesToExecute = this.services.values.filter((service) => service.enabled);
      servicesToExecute?.forEach((service) => {
        this.executeService(service);
      });

      icon.classList.add('--show');

      closeAlert();
      closeBoxManager();
    };

    const openBoxManager = (allowClose: boolean | null) => {
      allowClose = allowClose ?? false;

      const closeButton = boxManager.querySelector('.ecodigo-ckm-box-manager__close') as HTMLElement;

      alert.style.zIndex = '-1';
      boxManager.classList.add('--show');
      wrapper.classList.add('--show-backdrop', '--show');

      if (allowClose) {
        backdrop.addEventListener('click', handleCloseBoxManager);
      } else {
        closeButton.style.visibility = 'hidden';
      }

      body.forEach((item) => {
        item.style.overflowY = 'hidden';
      });
    };

    const allowAll = () => {
      boxManager
        .querySelectorAll('.ecodigo-ckm-box-manager__services-list__item__header__switch .ecodigo-ckm-switch__input')
        .forEach((checkbox) => {
          (checkbox as HTMLInputElement).checked = true;
          checkbox.dispatchEvent(new Event('change'));
        });

      boxManager.querySelector('[data-action=confirm]')?.dispatchEvent(new Event('click'));
    };

    // Event Listeners
    if (alert && boxManager) {
      // Open BoxManager if user click on "Customize"
      alert.querySelector('#alert-customize')?.addEventListener('click', (event) => {
        event.preventDefault();
        openBoxManager(false);
      });
    }

    if (icon && boxManager) {
      // Open BoxManager when we click on Icon
      icon.addEventListener('click', (event) => {
        event.preventDefault();
        openBoxManager(true);
      });
    }

    if (boxManager) {
      // Click on save
      boxManager.querySelector('[data-action=confirm]')?.addEventListener('click', (event) => {
        event.preventDefault();
        saveOptions();
      });

      // Click on close
      boxManager.querySelector('.ecodigo-ckm-box-manager__close')?.addEventListener('click', (event) => {
        event.preventDefault();
        closeBoxManager();
      });

      // Click on allowAll into BoxManager
      boxManager.querySelectorAll('[data-action=allowAll]').forEach((element) => {
        element.addEventListener('click', (event) => {
          event.preventDefault();
          allowAll();
        });
      });

      // Toggle pane on BoxManager
      boxManager.querySelectorAll('.ecodigo-ckm-box-manager__services-list__item__header')?.forEach((header) => {
        header.addEventListener('click', (event) => {
          const checkboxWrapper = header.querySelector('.ecodigo-ckm-box-manager__services-list__item__header__switch');
          const checkbox = checkboxWrapper?.querySelector('input');
          if (event.target === checkbox || event.target === checkboxWrapper) {
            return; // Don't toggle item if user clicked on checkbox or his wrapper
          }
          const parent = header.closest('.ecodigo-ckm-box-manager__services-list__item');
          parent?.classList.toggle('--opened');
        });
      });
    }

    if (alert) {
      // Click on allow all on Alert
      alert.querySelectorAll('[data-action=allowAll]').forEach((element) => {
        element.addEventListener('click', (event) => {
          event.preventDefault();
          allowAll();
          saveOptions();
        });
      });
    }

    if (boxManager) {
      // Set correct options from cookies
      this.populateOptions(boxManager);
    }

    if (icon && alert) {
      // Open Alert if use have defined no cookies
      if (this.readCookie().length === 0) {
        openAlert();
      } else {
        icon.classList.add('--show');
      }
    }
  }
}

export default ConsentBoxManager;
